import { useState, type FormEvent } from 'react';
import { Leaf } from 'lucide-react';
import { PrimaryButton } from '../components/ui';
import { RestoreBackupControl } from '../components/RestoreBackupControl';
import { useUserProfile } from '../hooks/useUserProfile';
import { MAX_DISPLAY_NAME_LENGTH } from '../lib/userProfile';
import {
  canSubmitProfileName,
  createProfileFromSubmission,
} from '../lib/profileExperience';

export function FirstRun(): JSX.Element {
  const { createProfile, refreshProfile } = useUserProfile();
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restoreMessage, setRestoreMessage] = useState<string | null>(null);
  const valid = canSubmitProfileName(name, false);

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      createProfileFromSubmission(name, submitting, createProfile);
    } catch (submissionError) {
      setError(
        submissionError instanceof Error
          ? submissionError.message
          : 'Your profile could not be saved. Please try again.',
      );
      setSubmitting(false);
    }
  };

  return (
    <main className="first-run">
      <section className="first-run__card" aria-labelledby="first-run-title">
        <div className="first-run__mark" aria-hidden="true">
          <Leaf size={24} strokeWidth={1.8} />
          <span>CR</span>
        </div>
        <p className="first-run__eyebrow">Conscious Ritual</p>
        <h1 id="first-run-title">Welcome</h1>
        <form className="first-run__form" onSubmit={submit}>
          <label htmlFor="profile-name">What is your name?</label>
          <input
            id="profile-name"
            name="displayName"
            type="text"
            autoComplete="name"
            autoFocus
            maxLength={MAX_DISPLAY_NAME_LENGTH}
            value={name}
            onChange={(event) => setName(event.target.value)}
            aria-describedby={error ? 'profile-local-note profile-error' : 'profile-local-note'}
          />
          <p id="profile-local-note" className="first-run__note">
            Your profile is stored locally on this device.
          </p>
          {error ? (
            <p id="profile-error" className="form-error" role="alert">
              {error}
            </p>
          ) : null}
          <PrimaryButton type="submit" block large disabled={!valid || submitting}>
            {submitting ? 'Saving…' : 'Continue'}
          </PrimaryButton>
        </form>
        <div className="first-run__restore" aria-labelledby="restore-existing-title">
          <p id="restore-existing-title" className="first-run__restore-title">Already use Conscious Ritual?</p>
          <RestoreBackupControl
            buttonLabel="Restore Existing Backup"
            successMessage="Backup restored. Your local data is ready."
            onRestoreComplete={(backup) => {
              refreshProfile();
              if (!backup.profile) {
                setRestoreMessage('Backup restored, but it did not include a profile. Enter your name above to continue.');
              }
            }}
          />
          {restoreMessage ? <p className="first-run__note" role="status">{restoreMessage}</p> : null}
        </div>
      </section>
    </main>
  );
}
